import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Services
import UserService from "services/user-service";
import WorkspaceService from "services/workspace-service";
import AuthService from "services/auth-service"; // [NEW] For current user ID

// Components
import CreateWorkspaceModal from "layouts/dashboard/components/CreateWorkspaceModal";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]

// Role descriptions sourced from workspace role seed
const ROLE_DESCRIPTIONS = {
    Admin: "Full workspace management, excluding billing.",
    Manager: "Operational lead. Can manage profiles, snapshots and reports.",
    Analyst: "Data insight specialist. Can view all data and generate reports.",
    Viewer: "Read-only access to stats and dashboards.",
};

function Workspaces() {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [currentUser, setCurrentUser] = useState(null); // [NEW] For ID comparison

    // --- Switch Workspace State ---
    const [confirmSwitchWorkspace, setConfirmSwitchWorkspace] = useState(null);

    // Invitation State
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("");
    const [inviting, setInviting] = useState(false);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [loadingInvitations, setLoadingInvitations] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // [NEW] Demo Mode Restriction
    const [showDemoPopup, setShowDemoPopup] = useState(false);
    const [demoFeature, setDemoFeature] = useState("");

    // Manager State (Team Members)
    const [manageWorkspace, setManageWorkspace] = useState(null); // The workspace object being managed
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [invitationToRemove, setInvitationToRemove] = useState(null);
    const [memberSearch, setMemberSearch] = useState("");
    const [invitationSearch, setInvitationSearch] = useState("");

    // [NEW] RBAC Management helpers
    const [manageRole, setManageRole] = useState(null); // The role of the person OPENING the drawer

    // Edit Workspace State
    const [editWorkspace, setEditWorkspace] = useState(null); // Workspace being edited
    const [editName, setEditName] = useState("");
    const [editSaving, setEditSaving] = useState(false);
    const [editMessage, setEditMessage] = useState({ type: "", text: "" });
    // Transfer ownership
    const [editMembers, setEditMembers] = useState([]);
    const [selectedTransferUser, setSelectedTransferUser] = useState("");
    const [openTransferDialog, setOpenTransferDialog] = useState(false);
    const [transferring, setTransferring] = useState(false);
    // Delete workspace
    const [deleteConfirmSlug, setDeleteConfirmSlug] = useState("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchWorkspaces();
        fetchCurrentUser();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const res = await AuthService.getProfile();
            setCurrentUser(res.data); // Stores type: 'users', id: '...', attributes: {...}
        } catch (err) {
            console.error("Failed to fetch current user", err);
        }
    };
    const fetchWorkspaces = async () => {
        try {
            setLoading(true);
            const response = await UserService.fetchUserWorkspaces(); // Returns array of user-workspace mappings
            setWorkspaces(response || []);

            // Find active workspace
            const active = response.find(w => w.is_active);
            if (active) {
                setActiveWorkspaceId(active.workspace_id._id);
                setUserRole(active.role);
                loadInvitations(active.workspace_id._id, active.role);
            }
        } catch (error) {
            console.error("Failed to fetch workspaces", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchWorkspace = (workspace) => {
        setConfirmSwitchWorkspace(workspace);
    };

    const confirmSwitch = async () => {
        if (!confirmSwitchWorkspace) return;
        try {
            await UserService.switchActiveWorkspace(confirmSwitchWorkspace._id);

            // ✅ Sync activeWorkspaceId to localStorage for immediate API reflects
            localStorage.setItem("activeWorkspaceId", confirmSwitchWorkspace._id);

            // 🔥 Soft refresh: notify other components (Header, Navbar) to update their context
            window.dispatchEvent(new CustomEvent("profile-updated"));

            // Refresh local state to update the table (active checkmark movement)
            await fetchWorkspaces();
        } catch (error) {
            console.error("Failed to switch workspace", error);
        } finally {
            setConfirmSwitchWorkspace(null);
        }
    };

    const loadInvitations = async (workspaceId, role) => {
        if (role !== "Owner" && role !== "Admin") return;
        try {
            setLoadingInvitations(true);
            const res = await WorkspaceService.fetchInvitations(workspaceId);
            setPendingInvitations(res || []);
        } catch (error) {
            console.error("Failed to load invitations", error);
        } finally {
            setLoadingInvitations(false);
        }
    };

    const loadMembers = async (workspaceId) => {
        try {
            setLoadingMembers(true);
            const res = await WorkspaceService.fetchWorkspaceMembers(workspaceId);
            setMembers(res || []);
        } catch (error) {
            console.error("Failed to load members", error);
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            await WorkspaceService.updateMemberRole(manageWorkspace._id, userId, newRole);
            loadMembers(manageWorkspace._id);
            setMessage({ type: "success", text: "Role updated successfully." });
        } catch (error) {
            console.error("Failed to update role", error);
            setMessage({ type: "error", text: "Failed to update member role." });
        }
    };

    const handleRemoveMember = (member) => {
        setMemberToRemove(member);
        setOpenRemoveDialog(true);
    };

    const handleConfirmRemove = async () => {
        if (!memberToRemove && !invitationToRemove) return;

        try {
            if (memberToRemove) {
                await WorkspaceService.removeMember(manageWorkspace._id, memberToRemove.user_id._id);
            } else if (invitationToRemove) {
                await WorkspaceService.deleteInvitation(manageWorkspace._id, invitationToRemove._id);
            }

            loadMembers(manageWorkspace._id);
            const role = workspaces.find(w => w.workspace_id._id === manageWorkspace._id)?.role;
            loadInvitations(manageWorkspace._id, role);

            setMessage({
                type: "success",
                text: `${memberToRemove ? "Member" : "Invitation"} removed successfully.`
            });
            fetchWorkspaces();
        } catch (error) {
            console.error("Failed to remove", error);
            setMessage({
                type: "error",
                text: `Failed to remove ${memberToRemove ? "member" : "invitation"}.`
            });
        } finally {
            setOpenRemoveDialog(false);
            setMemberToRemove(null);
            setInvitationToRemove(null);
        }
    };

    const handleRemoveInvitation = (invitation) => {
        setInvitationToRemove(invitation);
        setMemberToRemove(null);
        setOpenRemoveDialog(true);
    };

    const handleInvite = async () => {
        if (!inviteEmail) {
            setMessage({ type: "error", text: "Please enter an email address." });
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteEmail)) {
            setMessage({ type: "error", text: "Please enter a valid email address." });
            return;
        }

        try {
            setInviting(true);
            setMessage({ type: "", text: "" });
            await WorkspaceService.sendInvitation(manageWorkspace._id, inviteEmail, inviteRole);
            setMessage({ type: "success", text: `Invitation sent to ${inviteEmail}!` });
            setInviteEmail("");
            loadInvitations(manageWorkspace._id, "Admin");
            fetchWorkspaces();
        } catch (error) {
            console.error("Invitation error:", error);
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to send invitation."
            });
        } finally {
            setInviting(false);
        }
    };

    const renderWorkspacesTable = () => {
        if (workspaces.length === 0) {
            return (
                <Alert severity="info" sx={{ mt: 2 }}>
                    You are not a member of any workspaces.
                </Alert>
            );
        }

        return (
            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", mt: 2 }}>
                <Table>
                    <TableHead sx={{ display: "table-header-group", bgcolor: "#f0f2f5" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", py: 1.5 }} align="left">Workspace</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", py: 1.5 }} align="center">Role</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", py: 1.5 }} align="left">Team</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", py: 1.5 }} align="left">Created</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", py: 1.5 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {workspaces.map((uw) => {
                            const workspace = uw.workspace_id;
                            const isActive = uw.is_active;

                            return (
                                <TableRow
                                    key={uw._id}
                                    sx={{
                                        bgcolor: isActive ? "rgba(77, 182, 172, 0.04)" : "inherit",
                                        borderLeft: isActive ? "4px solid #4db6ac" : "none",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" }
                                    }}
                                >
                                    <TableCell sx={{ py: 1.5 }}>
                                        <MDBox>
                                            <MDTypography variant="subtitle2" fontWeight="bold">
                                                {workspace.name}
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                                                {workspace.slug}
                                            </MDTypography>
                                        </MDBox>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }} align="center">
                                        <MDBox
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "80px",
                                                height: "26px",
                                                borderRadius: "8px",
                                                fontSize: "0.75rem",
                                                fontWeight: "bold",
                                                bgcolor: uw.role === "Owner" ? "#4db6ac" : "transparent",
                                                border: uw.role === "Owner" ? "none" : "1px solid #ddd",
                                                color: uw.role === "Owner" ? "#344767" : "#6c757d",
                                            }}
                                        >
                                            {uw.role}
                                        </MDBox>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }} align="left">
                                        <MDBox display="flex" flexDirection="column">
                                            <MDTypography variant="caption" fontWeight="medium" color="text">
                                                {uw.member_count || 0} {uw.member_count === 1 ? "Member" : "Members"}
                                            </MDTypography>
                                            {uw.pending_invitation_count > 0 && (
                                                <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                                                    {uw.pending_invitation_count} Pending
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }} align="left">
                                        <MDTypography variant="caption" color="text">
                                            {workspace.created_at ? new Date(workspace.created_at).toLocaleDateString() : "N/A"}
                                        </MDTypography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 1.5 }}>
                                        <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                                            {isActive ? (
                                                <Tooltip title="Currently Active">
                                                    <MDBox
                                                        sx={{
                                                            width: 70, // Matches width of outlined button + margin
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            mr: 1
                                                        }}
                                                    >
                                                        <MDBox
                                                            sx={{
                                                                backgroundColor: "#26282b",
                                                                borderRadius: "50%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            <CheckCircleIcon color="accent" sx={{ fontSize: "20px !important" }} />
                                                        </MDBox>
                                                    </MDBox>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Switch to this workspace">
                                                    <MDButton
                                                        variant="outlined"
                                                        color="info"
                                                        size="small"
                                                        onClick={() => handleSwitchWorkspace(workspace)}
                                                        sx={{
                                                            mr: 1,
                                                            borderRadius: "12px",
                                                            px: 2,
                                                            minWidth: 70,
                                                            fontSize: "0.7rem",
                                                            fontWeight: "bold",
                                                            height: "26px",
                                                            borderWidth: "1px !important"
                                                        }}
                                                    >
                                                        Switch
                                                    </MDButton>
                                                </Tooltip>
                                            )}

                                            {uw.role === "Owner" ? (
                                                <Tooltip title="Edit Workspace">
                                                    <IconButton
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => handleOpenEdit(workspace)}
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        <Icon fontSize="small">edit</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <MDBox sx={{ width: 30, ml: 0.5 }} />
                                            )}

                                            {(uw.role === "Owner" || uw.role === "Admin") ? (
                                                <Tooltip title="Manage Team">
                                                    <IconButton
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => {
                                                            const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
                                                            if (isDemo) {
                                                                setDemoFeature("Managing Team");
                                                                setShowDemoPopup(true);
                                                                return;
                                                            }
                                                            setManageWorkspace(workspace);
                                                            setManageRole(uw.role);
                                                            loadInvitations(workspace._id, uw.role);
                                                            loadMembers(workspace._id);
                                                        }}
                                                    >
                                                        <Icon>groups</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <MDBox sx={{ width: 30 }} />
                                            )}
                                        </MDBox>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // ---- Edit Workspace Handlers ----
    const handleOpenEdit = async (workspace) => {
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setDemoFeature("Editing Workspace");
            setShowDemoPopup(true);
            return;
        }

        setEditWorkspace(workspace);
        setEditName(workspace.name);
        setEditMessage({ type: "", text: "" });
        setSelectedTransferUser("");
        setDeleteConfirmSlug("");
        // Load members for transfer dropdown
        try {
            const res = await WorkspaceService.fetchWorkspaceMembers(workspace._id);
            setEditMembers(res || []);
        } catch { setEditMembers([]); }
    };

    const handleRenameWorkspace = async () => {
        if (!editName.trim() || editName.trim() === editWorkspace.name) return;
        try {
            setEditSaving(true);
            await WorkspaceService.renameWorkspace(editWorkspace._id, editName.trim());
            setEditMessage({ type: "success", text: "Workspace renamed successfully." });
            await fetchWorkspaces();
        } catch (error) {
            setEditMessage({ type: "error", text: error.response?.data?.message || "Failed to rename workspace." });
        } finally {
            setEditSaving(false);
        }
    };

    const handleConfirmTransfer = async () => {
        if (!selectedTransferUser) return;
        try {
            setTransferring(true);
            await WorkspaceService.transferOwnership(editWorkspace._id, selectedTransferUser);
            setOpenTransferDialog(false);
            setEditWorkspace(null);
            setEditMessage({ type: "success", text: "Ownership transferred." });
            await fetchWorkspaces();
        } catch (error) {
            setOpenTransferDialog(false);
            setEditMessage({ type: "error", text: error.response?.data?.message || "Failed to transfer ownership." });
        } finally {
            setTransferring(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (deleteConfirmSlug !== editWorkspace.slug) return;
        const deletedId = editWorkspace._id;
        const wasActive = activeWorkspaceId === deletedId;
        try {
            setDeleting(true);
            await WorkspaceService.deleteWorkspace(deletedId);
            setOpenDeleteDialog(false);
            setEditWorkspace(null);

            // Find another workspace to switch to if we just deleted the active one
            const remaining = workspaces.filter(w => w.workspace_id._id !== deletedId);

            if (wasActive && remaining.length > 0) {
                // Switch to first remaining workspace, then soft-refresh the whole app
                const nextId = remaining[0].workspace_id._id;
                await UserService.switchActiveWorkspace(nextId);
                localStorage.setItem("activeWorkspaceId", nextId);
                window.dispatchEvent(new CustomEvent("profile-updated"));
                await fetchWorkspaces();
            } else if (wasActive && remaining.length === 0) {
                // No workspaces left — full reload so backend can reset state cleanly
                localStorage.removeItem("activeWorkspaceId");
                window.location.reload();
            } else {
                // Deleted a non-active workspace — just refresh the list
                await fetchWorkspaces();
            }
        } catch (error) {
            setOpenDeleteDialog(false);
            setEditMessage({ type: "error", text: error.response?.data?.message || "Failed to delete workspace." });
        } finally {
            setDeleting(false);
            setDeleteConfirmSlug("");
        }
    };


    return (
        <MDBox>
            <Card sx={{ mb: 3, mt: 7, overflow: "visible" }}>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={2}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <MDTypography variant="h6" color="white">
                        My Workspaces
                    </MDTypography>
                    <MDButton
                        variant="contained"
                        color="white"
                        size="small"
                        startIcon={<Icon>add</Icon>}
                        sx={{ color: "info.main" }}
                        onClick={() => {
                            const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
                            if (isDemo) {
                                setDemoFeature("Creating Workspace");
                                setShowDemoPopup(true);
                                return;
                            }
                            setOpenCreateModal(true);
                        }}
                    >
                        Create Workspace
                    </MDButton>
                </MDBox>

                <MDBox p={2}>
                    {loading ? (
                        <MDBox display="flex" justifyContent="center" py={5}>
                            <CircularProgress color="info" />
                        </MDBox>
                    ) : (
                        renderWorkspacesTable()
                    )}
                </MDBox>
            </Card>

            {/* Manage Team side panel */}
            <Drawer
                anchor="right"
                open={Boolean(manageWorkspace)}
                onClose={() => {
                    setManageWorkspace(null);
                    setMessage({ type: "", text: "" });
                }}
                PaperProps={{
                    sx: { width: { xs: "100%", sm: "450px" }, p: 3 }
                }}
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDBox>
                        <MDTypography variant="h5" fontWeight="bold">
                            Manage Team
                        </MDTypography>
                        <MDTypography variant="button" color="text">
                            Workspace: {manageWorkspace?.name}
                        </MDTypography>
                    </MDBox>
                    <IconButton onClick={() => setManageWorkspace(null)}>
                        <Icon>close</Icon>
                    </IconButton>
                </MDBox>

                <MDBox>
                    <Divider sx={{ mt: 0, mb: 1 }} />
                </MDBox>

                <MDBox mt={1}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                        Invite New Member
                    </MDTypography>
                    <MDBox component="form" role="form">
                        <MDBox mb={2}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="colleague@example.com"
                            />
                        </MDBox>
                        <MDBox mb={2}>
                            <FormControl fullWidth>
                                <InputLabel id="drawer-role-label" shrink>Role</InputLabel>
                                <Select
                                    labelId="drawer-role-label"
                                    id="drawer-role"
                                    displayEmpty
                                    notched
                                    value={inviteRole}
                                    label="Role"
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    renderValue={(val) =>
                                        val ? val : <span style={{ color: "rgba(0,0,0,0.38)" }}>Select a role…</span>
                                    }
                                    sx={{ height: "45px" }}
                                >
                                    <MenuItem value="Admin">
                                        <Tooltip title={ROLE_DESCRIPTIONS.Admin} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Admin</span></Tooltip>
                                    </MenuItem>
                                    <MenuItem value="Manager">
                                        <Tooltip title={ROLE_DESCRIPTIONS.Manager} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Manager</span></Tooltip>
                                    </MenuItem>
                                    <MenuItem value="Analyst">
                                        <Tooltip title={ROLE_DESCRIPTIONS.Analyst} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Analyst</span></Tooltip>
                                    </MenuItem>
                                    <MenuItem value="Viewer">
                                        <Tooltip title={ROLE_DESCRIPTIONS.Viewer} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Viewer</span></Tooltip>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </MDBox>
                        <MDButton
                            variant="gradient"
                            color="accent"
                            fullWidth
                            onClick={async () => {
                                if (!inviteEmail) {
                                    setMessage({ type: "error", text: "Please enter an email address." });
                                    return;
                                }

                                // Email validation
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                if (!emailRegex.test(inviteEmail)) {
                                    setMessage({ type: "error", text: "Please enter a valid email address." });
                                    return;
                                }

                                try {
                                    setInviting(true);
                                    setMessage({ type: "", text: "" });
                                    await WorkspaceService.sendInvitation(manageWorkspace._id, inviteEmail, inviteRole);
                                    setMessage({ type: "success", text: `Invitation sent to ${inviteEmail}!` });
                                    setInviteEmail("");
                                    const role = workspaces.find(w => w.workspace_id._id === manageWorkspace._id)?.role;
                                    loadInvitations(manageWorkspace._id, role);
                                } catch (error) {
                                    setMessage({
                                        type: "error",
                                        text: error.response?.data?.message || "Failed to send invitation."
                                    });
                                } finally {
                                    setInviting(false);
                                }
                            }}
                            disabled={inviting}
                            startIcon={inviting ? <CircularProgress size={20} color="inherit" /> : <Icon>send</Icon>}
                        >
                            {inviting ? "Sending..." : "Send Invitation"}
                        </MDButton>
                    </MDBox>

                    {message.text && (
                        <MDBox mt={2}>
                            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })} sx={{ fontSize: "0.875rem" }}>
                                {message.text}
                            </Alert>
                        </MDBox>
                    )}
                </MDBox>

                <MDBox mt={4} p={2} bgcolor="#f8f9fa" borderRadius="lg">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <MDTypography variant="subtitle2" fontWeight="bold">
                            Current Members
                        </MDTypography>
                        <MDBox width="150px">
                            <TextField
                                placeholder="Search..."
                                size="small"
                                fullWidth
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                sx={{
                                    "& .MuiInputBase-root": { fontSize: "0.75rem", height: "30px" },
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" }
                                }}
                            />
                        </MDBox>
                    </MDBox>

                    {loadingMembers ? (
                        <MDBox display="flex" justifyContent="center" py={3}>
                            <CircularProgress color="info" size={24} />
                        </MDBox>
                    ) : members.filter(m => (m.user_id?.name || "").toLowerCase().includes(memberSearch.toLowerCase()) || (m.user_id?.email || "").toLowerCase().includes(memberSearch.toLowerCase())).length === 0 ? (
                        <MDTypography variant="button" color="text" fontStyle="italic">
                            {memberSearch ? "No members matching search." : "No members found."}
                        </MDTypography>
                    ) : (
                        <MDBox sx={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", bgcolor: "white" }}>
                            {members
                                .filter(m => (m.user_id?.name || "").toLowerCase().includes(memberSearch.toLowerCase()) || (m.user_id?.email || "").toLowerCase().includes(memberSearch.toLowerCase()))
                                .map((member, index, filtered) => {
                                    const user = member.user_id;
                                    const isOwner = member.role === "Owner";
                                    return (
                                        <MDBox
                                            key={member._id}
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            p={1.5}
                                            bgcolor="white"
                                            borderBottom={index === filtered.length - 1 ? "none" : "1px solid #eee"}
                                        >
                                            <MDBox display="flex" alignItems="center">
                                                <MDBox>
                                                    <MDTypography variant="subtitle2" fontWeight="medium" fontSize="0.875rem">
                                                        {user.name}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {user.email}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                            <MDBox display="flex" alignItems="center" justifyContent="flex-end" sx={{ minWidth: "150px" }}>
                                                {/* Role Column - Centered fixed width */}
                                                <MDBox width="90px" display="flex" justifyContent="center" mr={1}>
                                                    {isOwner ? (
                                                        <MDBox
                                                            sx={{
                                                                width: "100%",
                                                                height: "24px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "info.main",
                                                                color: "white",
                                                                borderRadius: "8px",
                                                                fontSize: "0.7rem",
                                                                fontWeight: "bold",
                                                                textTransform: "capitalize",
                                                                boxShadow: 1
                                                            }}
                                                        >
                                                            Owner
                                                        </MDBox>
                                                    ) : (
                                                        <Select
                                                            value={member.role}
                                                            size="small"
                                                            onChange={(e) => handleUpdateRole(user._id, e.target.value)}
                                                            disabled={manageRole === "Admin" && member.role === "Admin" && user._id !== currentUser?.id}
                                                            sx={{
                                                                width: "100%",
                                                                height: "24px",
                                                                fontSize: "0.7rem",
                                                                borderRadius: "8px",
                                                                "& .MuiSelect-select": {
                                                                    py: 0,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    textAlign: "center"
                                                                },
                                                                opacity: (manageRole === "Admin" && member.role === "Admin" && user._id !== currentUser?.id) ? 0.6 : 1
                                                            }}
                                                        >
                                                            <MenuItem value="Admin" sx={{ fontSize: "0.8rem" }}><Tooltip title={ROLE_DESCRIPTIONS.Admin} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Admin</span></Tooltip></MenuItem>
                                                            <MenuItem value="Manager" sx={{ fontSize: "0.8rem" }}><Tooltip title={ROLE_DESCRIPTIONS.Manager} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Manager</span></Tooltip></MenuItem>
                                                            <MenuItem value="Analyst" sx={{ fontSize: "0.8rem" }}><Tooltip title={ROLE_DESCRIPTIONS.Analyst} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Analyst</span></Tooltip></MenuItem>
                                                            <MenuItem value="Viewer" sx={{ fontSize: "0.8rem" }}><Tooltip title={ROLE_DESCRIPTIONS.Viewer} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Viewer</span></Tooltip></MenuItem>
                                                        </Select>
                                                    )}
                                                </MDBox>

                                                {/* Actions Column */}
                                                <MDBox width="40px" display="flex" justifyContent="center">
                                                    {(manageRole === "Owner" ||
                                                        (manageRole === "Admin" && (user._id === currentUser?.id || member.role !== "Admin"))) && !isOwner && (
                                                            <Tooltip title={user._id === currentUser?.id ? "Leave Workspace" : "Remove Member"}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleRemoveMember(member)}
                                                                >
                                                                    <Icon fontSize="inherit">delete</Icon>
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                </MDBox>
                                            </MDBox>
                                        </MDBox>
                                    );
                                })}
                        </MDBox>
                    )}
                </MDBox>

                <MDBox mt={4} p={2} bgcolor="#f8f9fa" borderRadius="lg">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <MDTypography variant="subtitle2" fontWeight="bold">
                            Pending Invitations
                        </MDTypography>
                        <MDBox width="150px">
                            <TextField
                                placeholder="Search..."
                                size="small"
                                fullWidth
                                value={invitationSearch}
                                onChange={(e) => setInvitationSearch(e.target.value)}
                                sx={{
                                    "& .MuiInputBase-root": { fontSize: "0.75rem", height: "30px" },
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd" }
                                }}
                            />
                        </MDBox>
                    </MDBox>

                    {loadingInvitations ? (
                        <MDBox display="flex" justifyContent="center" py={3}>
                            <CircularProgress color="info" size={24} />
                        </MDBox>
                    ) : pendingInvitations.filter(inv => (inv.email || "").toLowerCase().includes(invitationSearch.toLowerCase())).length === 0 ? (
                        <MDTypography variant="button" color="text" fontStyle="italic">
                            {invitationSearch ? "No invitations matching search." : "No pending invitations."}
                        </MDTypography>
                    ) : (
                        <MDBox sx={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", bgcolor: "white" }}>
                            {pendingInvitations
                                .filter(inv => (inv.email || "").toLowerCase().includes(invitationSearch.toLowerCase()))
                                .map((inv, index, filtered) => (
                                    <MDBox
                                        key={inv._id}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        p={1.5}
                                        bgcolor="white"
                                        borderBottom={index === filtered.length - 1 ? "none" : "1px solid #eee"}
                                    >
                                        <MDBox>
                                            <MDTypography variant="subtitle2" fontWeight="medium" fontSize="0.875rem">
                                                {inv.email}
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                Expires: {new Date(inv.expires_at).toLocaleDateString()}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" alignItems="center">
                                            <Chip
                                                label={inv.role}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: "24px",
                                                    fontSize: "0.7rem",
                                                    borderRadius: "4px",
                                                    "& .MuiChip-label": { px: 1 },
                                                    mr: 1
                                                }}
                                            />
                                            <IconButton size="small" color="error" onClick={() => handleRemoveInvitation(inv)}>
                                                <Icon>delete</Icon>
                                            </IconButton>
                                        </MDBox>
                                    </MDBox>
                                ))}
                        </MDBox>
                    )}
                </MDBox>

                <Dialog open={openRemoveDialog} onClose={() => {
                    setOpenRemoveDialog(false);
                    setMemberToRemove(null);
                    setInvitationToRemove(null);
                }}>
                    <DialogTitle>Confirm {memberToRemove ? "Member" : "Invitation"} Removal</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove <strong>{memberToRemove ? memberToRemove.user_id.name : invitationToRemove?.email}</strong> from this workspace?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenRemoveDialog(false)} color="primary">Cancel</Button>
                        <Button onClick={handleConfirmRemove} color="error">Remove</Button>
                    </DialogActions>
                </Dialog>
            </Drawer>


            {/* Create Workspace Modal (Reused) */}
            <CreateWorkspaceModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                user={{ name: "User" }} // Optional: passing user object if needed, using generic or context would be better but keeping it simple as it's just for display name
            />

            {/* ===== Edit Workspace Drawer ===== */}
            <Drawer
                anchor="right"
                open={!!editWorkspace}
                onClose={() => setEditWorkspace(null)}
                PaperProps={{ sx: { width: { xs: "100%", sm: "450px" }, p: 3 } }}
            >
                {/* Header */}
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDBox>
                        <MDTypography variant="h5" fontWeight="bold">Edit Workspace</MDTypography>
                        <MDTypography variant="button" color="text">{editWorkspace?.name}</MDTypography>
                    </MDBox>
                    <IconButton onClick={() => setEditWorkspace(null)}>
                        <Icon>close</Icon>
                    </IconButton>
                </MDBox>

                <MDBox>
                    <Divider sx={{ mt: 0, mb: 1 }} />
                </MDBox>

                {editMessage.text && (
                    <Alert severity={editMessage.type || "info"} sx={{ mb: 2 }} onClose={() => setEditMessage({ type: "", text: "" })}>
                        {editMessage.text}
                    </Alert>
                )}

                {/* ---- Rename Section ---- */}
                <MDBox mt={1}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>Rename Workspace</MDTypography>
                    <MDBox display="flex" gap={1} alignItems="center">
                        <TextField
                            fullWidth
                            size="small"
                            label="Workspace Name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                        <MDButton
                            variant="gradient"
                            color="accent"
                            size="small"
                            onClick={handleRenameWorkspace}
                            disabled={editSaving || !editName.trim() || editName.trim() === editWorkspace?.name}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                        >
                            {editSaving ? <CircularProgress size={16} color="inherit" /> : "Save"}
                        </MDButton>
                    </MDBox>
                </MDBox>

                <MDBox mt={3}>
                    <Divider sx={{ mt: 0, mb: 2 }} />
                </MDBox>

                {/* ---- Transfer Ownership Section ---- */}
                <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>Transfer Ownership</MDTypography>
                    {editMembers.filter(m => m.role === "Admin").length === 0 ? (
                        <Tooltip title="Promote a member to Admin first." placement="right">
                            <span>
                                <MDButton variant="gradient" color="accent" fullWidth disabled>
                                    No Admins Available
                                </MDButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <MDBox display="flex" gap={1} alignItems="center">
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Admin to Transfer To</InputLabel>
                                <Select
                                    value={selectedTransferUser}
                                    label="Select Admin to Transfer To"
                                    onChange={(e) => setSelectedTransferUser(e.target.value)}
                                    sx={{ height: "40px" }}
                                >
                                    {editMembers
                                        .filter(m => m.role === "Admin")
                                        .map(m => (
                                            <MenuItem key={m.user_id._id} value={m.user_id._id}>
                                                {m.user_id.name || m.user_id.email}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                            <MDButton
                                variant="gradient"
                                color="accent"
                                size="small"
                                disabled={!selectedTransferUser}
                                onClick={() => setOpenTransferDialog(true)}
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                                Transfer
                            </MDButton>
                        </MDBox>
                    )}
                </MDBox>

                <MDBox mt={3}>
                    <Divider sx={{ mt: 0, mb: 2 }} />
                </MDBox>

                {/* ---- Danger Zone ---- */}
                <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" color="error" mb={1}>
                        Danger Zone
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2, whiteSpace: "normal", opacity: 0.75 }}>
                        Permanently removes all members, invitations and cancels the active subscription. Cannot be undone.
                    </MDTypography>
                    <MDButton
                        variant="gradient"
                        color="error"
                        fullWidth
                        onClick={() => { setDeleteConfirmSlug(""); setOpenDeleteDialog(true); }}
                    >
                        Delete Workspace
                    </MDButton>
                </MDBox>
            </Drawer>


            {/* ===== Transfer Ownership Confirmation Dialog ===== */}
            <Dialog open={openTransferDialog} onClose={() => setOpenTransferDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Transfer Ownership?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are transferring ownership of <strong>{editWorkspace?.name}</strong> to <strong>
                            {editMembers.find(m => m.user_id._id === selectedTransferUser)?.user_id?.name ||
                                editMembers.find(m => m.user_id._id === selectedTransferUser)?.user_id?.email}
                        </strong>. You will become an Admin. This action can only be reversed by the new Owner.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTransferDialog(false)} disabled={transferring}>Cancel</Button>
                    <Button onClick={handleConfirmTransfer} color="warning" variant="contained" disabled={transferring}>
                        {transferring ? <CircularProgress size={16} color="inherit" /> : "Yes, Transfer"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== Delete Workspace Confirmation Dialog ===== */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: "error.main" }}>Delete Workspace?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1.5 }}>
                        This will permanently delete <strong>{editWorkspace?.name}</strong> and remove all members and pending invitations.
                    </DialogContentText>
                    <Alert severity="warning" sx={{ mb: 2, fontSize: "0.8rem" }}>
                        If this workspace has an active subscription, it will be <strong>automatically cancelled at the end of the current billing period</strong>. You will not be charged again. This cannot be undone.
                    </Alert>
                    <DialogContentText sx={{ mb: 1 }}>
                        Type the workspace slug to confirm:
                    </DialogContentText>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 1, fontFamily: "monospace", bgcolor: "#f5f5f5", p: 0.5, borderRadius: 1 }}>
                        {editWorkspace?.slug}
                    </MDTypography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={editWorkspace?.slug}
                        value={deleteConfirmSlug}
                        onChange={(e) => setDeleteConfirmSlug(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleting}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting || deleteConfirmSlug !== editWorkspace?.slug}
                    >
                        {deleting ? <CircularProgress size={16} color="inherit" /> : "Delete Forever"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== Switch Workspace Confirmation Dialog ===== */}
            <Dialog open={Boolean(confirmSwitchWorkspace)} onClose={() => setConfirmSwitchWorkspace(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Workspace Switch</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to switch to <strong>{confirmSwitchWorkspace?.workspace_id?.name || confirmSwitchWorkspace?.name || "this workspace"}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setConfirmSwitchWorkspace(null)}>
                        CANCEL
                    </Button>
                    <Button color="error" onClick={confirmSwitch}>
                        SWITCH
                    </Button>
                </DialogActions>
            </Dialog>

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName={demoFeature}
            />
        </MDBox>

    );
}

export default Workspaces;
